import { useMemo } from 'react';
import styled from 'styled-components';
import { localizeDate } from '../../utils/datetime';
import { FlexColumn } from '../Flex/FlexColumn';

const Wrapper = styled(FlexColumn)<{
  $padding: string | undefined;
  $hideBorder: boolean | undefined;
}>`
  width: 100%;
  border-radius: 8px;
  ${({ theme, $hideBorder }) =>
    !!$hideBorder && `border: 1px solid ${theme.palette.common.gray}`};
  background-color: ${({ theme }) => theme.palette.common.gray3};
  padding: ${({ $padding }) => $padding ?? '14px 30px'};
  overflow: auto;
`;

const Table = styled.table`
  overflow-y: auto;
  max-height: 280px;
  border-collapse: collapse;

  & th,
  & td {
    border: ${({ theme }) => `1px solid ${theme.palette.common.gray}`};
    padding: 10px 24px;
    font-weight: 400;
    font-size: 13px;
  }

  & th {
    color: ${({ theme }) => theme.palette.common.gray2};
  }
`;

type Column = { id: string; display_name: string; type?: string };

type Props = {
  data: { [k: string]: string | number | boolean | object }[];
  columns?: Column[];
  padding?: string;
  hideBorder?: boolean;
};

const CondensedTable = ({ data, columns, padding, hideBorder }: Props) => {
  const columnsMap: Column[] = useMemo(
    () =>
      columns ??
      Array.from(
        new Set(
          data?.reduce?.(
            (acc: any, item: any) => [...acc, ...Object.keys(item || {})],
            [],
          ),
        ),
      ).map((key: string) => ({ id: key, display_name: key })),
    [columns, data],
  );

  if (data.length === 0) {
    return null;
  }

  return (
    <Wrapper $padding={padding} $hideBorder={hideBorder}>
      {columnsMap?.length > 0 && (
        <Table>
          <thead>
            <tr role="row">
              {columnsMap.map((column) => (
                <th role="columnheader" key={column.id}>
                  {column.display_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr role="row" key={idx}>
                {columnsMap.map((column, colIdx) => (
                  <td role="cell" key={`${idx}_${column.id}`}>
                    {columnsMap[colIdx]?.type === 'date'
                      ? localizeDate(row[column.id]?.toString()).format('L LT')
                      : row[column.id]?.toString() || ' '}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Wrapper>
  );
};

export default CondensedTable;
